// Admin Quality Routing — Scott's screen over Merlin's tracked quality threads.
// Backend: app/routes/signals_reports.py (/api/admin/signals/threads-tracked).

import api from './api';

// Mirrors db/models/signal_threads.py SignalThreadStatus. 'completed' is the
// Done lane: a REAL issue that was handled (distinct from 'dismissed' = not an
// issue). A status the backend adds later still gets a readable pill via
// statusLabel()'s fallback instead of rendering blank.
export type ThreadStatus = 'open' | 'routed' | 'in_investigation' | 'dismissed' | 'completed';

export interface TrackedThread {
  id: string;
  thread_no: string;
  thread_key: string;
  title: string;
  summary: string;
  recommended_action: string;
  status: ThreadStatus;
  source_tag: 'sales_site' | 'signal_suspected';
  severity: string;
  work_state: string;
  work_state_note: string;
  signal_ids: string[];
  quality_issue_ids: string[];
  routed_to: string | null;
  routing_reason: string | null;
  investigation_id: string | null;
  dismissed_reason: string | null;
  routed_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  /** Proposal 12: the quality door's lane, computed server-side from status
   *  and routee. Older backends omit it (and the fields below). */
  desk_location?: DeskLocation;
  /** The case's INV number and whose court it is in, when in a case. */
  case_no?: string | null;
  case_court?: string | null;
  /** The asks Merlin sent on the thread, oldest first. */
  asks?: ThreadAsk[];
}

export type DeskLocation = 'triage' | 'cases' | 'done' | 'routed_away';

export interface ThreadAsk {
  target_name: string;
  sent_at: string | null;
  /** true: went out; false: the send failed, nothing went out; null: not attempted yet. */
  delivered: boolean | null;
  answered_at: string | null;
  answer_unseen: boolean;
}

export interface TrackedThreadsResponse {
  items: TrackedThread[];
  total: number;
}

export interface ThreadTimelineEvent {
  event_type: string;
  summary: string;
  actor_kind: 'merlin' | 'human' | 'system';
  created_at: string;
}

const BASE = '/admin/signals/threads-tracked';

export const qualityRoutingApi = {
  list: async (statusFilter?: string): Promise<TrackedThreadsResponse> => {
    const r = await api.get<TrackedThreadsResponse>(BASE, {
      params: statusFilter ? { status_filter: statusFilter } : undefined,
    });
    return r.data;
  },
  timeline: async (threadId: string): Promise<ThreadTimelineEvent[]> => {
    const r = await api.get<ThreadTimelineEvent[]>(`${BASE}/${threadId}/timeline`);
    return r.data;
  },
  route: async (
    threadId: string,
    routeTo: string,
    note?: string,
  ): Promise<TrackedThread> => {
    const r = await api.post<TrackedThread>(`${BASE}/${threadId}/route`, {
      route_to: routeTo,
      note: note || null,
    });
    return r.data;
  },
  dismiss: async (threadId: string, reason: string): Promise<TrackedThread> => {
    const r = await api.post<TrackedThread>(`${BASE}/${threadId}/dismiss`, {
      reason,
    });
    return r.data;
  },
};

// --- Recorded routing instructions (kanban 143454d3) --------------------------
// Backend: app/routes/signals_reports.py GET/POST /api/admin/signals/routing-instructions.

export type RoutingInstructionKind = 'route_to' | 'not_mine';
export type RoutingInstructionStatus = 'active' | 'retired';

export interface RoutingInstruction {
  id: string;
  /** "RI-1a2b3c4d" — how Merlin quotes the rule in a routing reason. */
  short_id: string;
  thread_id: string | null;
  thread_no: string;
  thread_title: string;
  kind: RoutingInstructionKind;
  /** Who items like this go to (route_to); null for not_mine. */
  route_to: string | null;
  route_roles: Array<Record<string, unknown>> | null;
  /** The author's words: who should get this kind of item and why. */
  reason: string;
  recorded_by: string;
  recorded_by_name: string;
  status: RoutingInstructionStatus;
  retired_by: string | null;
  retired_at: string | null;
  retired_reason: string | null;
  /** Whether Merlin's Report Memory took the learning copy. */
  memory_write_status: 'pending' | 'written' | 'failed';
  memory_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface RoutingInstructionListResponse {
  items: RoutingInstruction[];
  total: number;
  counts: Partial<Record<RoutingInstructionStatus, number>>;
}

const INSTRUCTIONS = '/admin/signals/routing-instructions';

export const routingInstructionsApi = {
  list: async (status?: RoutingInstructionStatus): Promise<RoutingInstructionListResponse> => {
    const r = await api.get<RoutingInstructionListResponse>(INSTRUCTIONS, {
      params: status ? { status_filter: status } : undefined,
    });
    return r.data;
  },
  retire: async (id: string, reason?: string): Promise<RoutingInstruction> => {
    const r = await api.post<RoutingInstruction>(`${INSTRUCTIONS}/${id}/retire`, {
      reason: reason || null,
    });
    return r.data;
  },
};

/** "Items like this go to Laura Caswell" / "Items like this are not Jess's". */
export function instructionRuleLine(row: Pick<RoutingInstruction, 'kind' | 'route_to' | 'recorded_by_name'>): string {
  if (row.kind === 'route_to' && row.route_to) return `Items like this go to ${row.route_to}`;
  return `Items like this are not ${row.recorded_by_name}'s`;
}

/** "Merlin told" / "Merlin NOT told — <why>" — the learning copy's fate. */
export function memoryLabel(row: Pick<RoutingInstruction, 'memory_write_status' | 'memory_error'>): string {
  if (row.memory_write_status === 'written') return 'Merlin told';
  if (row.memory_write_status === 'failed') return `Merlin NOT told${row.memory_error ? ` — ${row.memory_error}` : ''}`;
  return 'Telling Merlin…';
}

// --- pure view logic (unit-tested) -------------------------------------------

// The screen's lanes. "Needs routing" is the pile Scott works down; everything
// else is visibility into where threads went.
export type Lane = 'all' | 'needs_routing' | 'routed' | 'in_cases' | 'done' | 'dismissed';

export const LANE_LABELS: Record<Lane, string> = {
  all: 'All',
  needs_routing: 'Needs routing',
  routed: 'Routed',
  in_cases: 'In cases',
  done: 'Done',
  dismissed: 'Dismissed',
};

// Status pill text. Kept beside the lane map so the two can never drift apart
// again (the 'completed' pill rendered blank when only the lane side knew it).
export const STATUS_LABELS: Record<ThreadStatus, string> = {
  open: 'Needs routing',
  routed: 'Routed',
  in_investigation: 'In a case',
  completed: 'Done',
  dismissed: 'Dismissed',
};

// Never an empty pill: an unmapped status falls back to its raw value,
// humanized ("some_new_state" -> "Some new state").
export function statusLabel(status: string): string {
  const known = (STATUS_LABELS as Record<string, string | undefined>)[status];
  if (known) return known;
  const raw = status.trim().replace(/_/g, ' ');
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unknown';
}

export function laneOf(thread: Pick<TrackedThread, 'status'>): Exclude<Lane, 'all'> {
  switch (thread.status) {
    case 'open':
      return 'needs_routing';
    case 'routed':
      return 'routed';
    case 'in_investigation':
      return 'in_cases';
    case 'completed':
      return 'done';
    case 'dismissed':
      return 'dismissed';
  }
}

export function filterThreads(
  threads: TrackedThread[],
  lane: Lane,
  query: string,
): TrackedThread[] {
  const q = query.trim().toLowerCase();
  return threads.filter((t) => {
    if (lane !== 'all' && laneOf(t) !== lane) return false;
    if (!q) return true;
    return [t.thread_no, t.title, t.routed_to ?? '', t.routing_reason ?? '']
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
}

export function laneCounts(threads: TrackedThread[]): Record<Lane, number> {
  const counts: Record<Lane, number> = {
    all: threads.length,
    needs_routing: 0,
    routed: 0,
    in_cases: 0,
    done: 0,
    dismissed: 0,
  };
  for (const t of threads) counts[laneOf(t)] += 1;
  return counts;
}

// "3d ago" — coarse on purpose; the row is a scent, the detail has real dates.
export function relativeAge(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.floor((now.getTime() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
